/**
 * Chat Service
 * API service for Nexgo Concierge Chat API
 */

import axios, { AxiosError } from 'axios';
import {
  ChatRequest,
  ChatResponse,
  FollowUpsRequest,
  FollowUpsResponse,
  ExperiencesRequest,
  ExperiencesResponse,
  StreamEvent,
} from '@/types/chatTypes';
import { ERROR_MESSAGES } from '@/constants/errorMessages';

// API Configuration - Use Next.js API route for staging to avoid CORS issues
const CHAT_API_BASE_URL = '';
const CHAT_ENDPOINT = '/api/concierge-staging'; // Legacy merged endpoint
const CHAT_RESPONSE_ENDPOINT = '/api/chat/response';
const CHAT_FOLLOW_UPS_ENDPOINT = '/api/chat/follow-ups';
const CHAT_EXPERIENCES_ENDPOINT = '/api/chat/experiences';

// Create axios instance with default config
const chatApi = axios.create({
  baseURL: CHAT_API_BASE_URL,
  timeout: 60000, // 60 seconds timeout for chat responses (increased from 30s)
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging in development
chatApi.interceptors.request.use(
  config => {
    if (process.env.NODE_ENV === 'development') {
      const requestData = config.data as ChatRequest;

      // Log each field separately for better visibility - using explicit strings
      console.log('═══════════════════════════════════════════════════════');
      console.log('📡 [API] Chat API Request Details');
      console.log('═══════════════════════════════════════════════════════');
      console.log('URL:', config.url);
      console.log('Method:', config.method);
      console.log('user_id:', requestData?.user_id);
      console.log('session_id:', requestData?.session_id);
      console.log('hasAudioMessage:', !!requestData?.audio_message);
      console.log(
        'audioMessageLength:',
        requestData?.audio_message?.length || 0
      );
      console.log(
        'audioMessageSizeKB:',
        requestData?.audio_message
          ? ((requestData.audio_message.length * 0.75) / 1024).toFixed(2)
          : '0'
      );

      // CRITICAL VERIFICATION
      console.log(
        '🔍 [API] VERIFICATION - Request data keys:',
        Object.keys(requestData || {})
      );
      console.log(
        '🔍 [API] VERIFICATION - Has audio_message key:',
        'audio_message' in (requestData || {})
      );
      console.log(
        '🔍 [API] VERIFICATION - audio_message value exists:',
        !!requestData?.audio_message
      );
      console.log(
        '🔍 [API] VERIFICATION - audio_message type:',
        typeof requestData?.audio_message
      );
      console.log(
        '🔍 [API] VERIFICATION - audio_message length:',
        requestData?.audio_message?.length || 'UNDEFINED'
      );

      if (requestData?.audio_message) {
        console.log(
          '🔍 [API] VERIFICATION - audio_message first 50 chars:',
          requestData.audio_message.substring(0, 50)
        );
        console.log(
          '🔍 [API] VERIFICATION - audio_message last 50 chars:',
          requestData.audio_message.substring(
            requestData.audio_message.length - 50
          )
        );
      } else {
        console.log(
          '❌ [API] VERIFICATION - audio_message is MISSING from request!'
        );
      }

      console.log('═══════════════════════════════════════════════════════');
    }
    return config;
  },
  error => {
    console.error('📡 [API] Chat API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging and error handling
chatApi.interceptors.response.use(
  response => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Chat API Response:', {
        status: response.status,
        data: response.data,
      });
    }
    return response;
  },
  error => {
    console.error('Chat API Response Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Send a chat message to the Nexgo Concierge API
 */
export async function sendChatMessage(
  request: ChatRequest
): Promise<ChatResponse> {
  try {
    const response = await chatApi.post<ChatResponse>(CHAT_ENDPOINT, request);

    // Validate response structure
    if (!response.data) {
      throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
    }

    const { session_id, user_id, response: chatResponse } = response.data;

    // Validate required fields
    if (!session_id || !user_id || !chatResponse) {
      throw new Error(ERROR_MESSAGES.MISSING_REQUIRED_FIELDS);
    }

    return response.data;
  } catch (error) {
    // Log API and parameters for error identification
    console.error('═══════════════════════════════════════════════════════');
    console.error('❌ [API ERROR] Chat API Error Details');
    console.error('═══════════════════════════════════════════════════════');
    console.error('API Endpoint:', CHAT_ENDPOINT);
    console.error('Full URL:', `${CHAT_API_BASE_URL}${CHAT_ENDPOINT}`);
    console.error('Request Method:', 'POST');

    // Log request parameters
    console.error('Request Parameters:');
    console.error('  - user_id:', request?.user_id);
    console.error('  - session_id:', request?.session_id);
    console.error(
      '  - message:',
      request?.message
        ? `${request.message.substring(0, 100)}${request.message.length > 100 ? '...' : ''}`
        : 'N/A'
    );
    console.error('  - message_length:', request?.message?.length || 0);
    console.error('  - has_audio_message:', !!request?.audio_message);
    console.error(
      '  - audio_message_length:',
      request?.audio_message?.length || 0
    );
    console.error(
      '  - audio_message_size_kb:',
      request?.audio_message
        ? ((request.audio_message.length * 0.75) / 1024).toFixed(2)
        : '0'
    );
    console.error('  - request_keys:', Object.keys(request || {}));

    // Enhanced error handling
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      // Log error response details
      console.error('Error Response Details:');
      console.error('  - Status:', axiosError.response?.status);
      console.error('  - Status Text:', axiosError.response?.statusText);
      console.error('  - Response Data:', axiosError.response?.data);
      console.error('  - Error Message:', axiosError.message);
      console.error('  - Error Code:', axiosError.code);
      console.error('  - Request URL:', axiosError.config?.url);
      console.error(
        '  - Request Method:',
        axiosError.config?.method?.toUpperCase()
      );
      console.error('  - Timeout:', axiosError.config?.timeout);
      console.error('═══════════════════════════════════════════════════════');

      // Check for timeout errors first (before HTTP status codes)
      // Axios timeout errors have code 'ECONNABORTED' and no response
      if (
        axiosError.code === 'ECONNABORTED' ||
        axiosError.message.includes('timeout') ||
        (!axiosError.response && axiosError.config?.timeout)
      ) {
        console.error('⏱️ [TIMEOUT] Request exceeded timeout limit');
        throw new Error(ERROR_MESSAGES.TIMEOUT);
      }

      // Handle different HTTP status codes
      switch (axiosError.response?.status) {
        case 400:
          throw new Error(ERROR_MESSAGES.BAD_REQUEST);
        case 401:
          throw new Error(ERROR_MESSAGES.UNAUTHORIZED);
        case 403:
          throw new Error(ERROR_MESSAGES.FORBIDDEN);
        case 429:
          throw new Error(ERROR_MESSAGES.TOO_MANY_REQUESTS);
        case 500:
          throw new Error(ERROR_MESSAGES.INTERNAL_SERVER_ERROR);
        case 502:
          throw new Error(ERROR_MESSAGES.BAD_GATEWAY);
        case 503:
          throw new Error(ERROR_MESSAGES.SERVICE_UNAVAILABLE);
        case 504:
          const timeoutErrorData = axiosError.response?.data as
            | { message?: string }
            | undefined;
          const timeoutMessage =
            timeoutErrorData?.message || ERROR_MESSAGES.GATEWAY_TIMEOUT;
          throw new Error(timeoutMessage);
        default:
          const errorData = axiosError.response?.data as
            | { message?: string }
            | undefined;
          const errorMessage = errorData?.message || axiosError.message;
          throw new Error(
            `${ERROR_MESSAGES.CHAT_SERVICE_ERROR}: ${errorMessage}`
          );
      }
    }

    // Handle network errors (non-Axios errors)
    if (error instanceof Error) {
      console.error('Network/General Error Details:');
      console.error('  - Error Type:', error.constructor.name);
      console.error('  - Error Message:', error.message);
      console.error('  - Error Stack:', error.stack);
      console.error('═══════════════════════════════════════════════════════');

      // Check for timeout in error message (fallback for non-Axios timeout errors)
      if (error.message.toLowerCase().includes('timeout')) {
        throw new Error(ERROR_MESSAGES.TIMEOUT);
      }
      if (error.message.includes('Network Error')) {
        throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
      }
    }

    // Fallback error
    console.error('Unknown Error Type:', typeof error);
    console.error('Error Object:', error);
    console.error('═══════════════════════════════════════════════════════');
    throw new Error(ERROR_MESSAGES.UNEXPECTED_ERROR);
  }
}

/**
 * Generate a unique session ID
 */
export function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate a unique user ID (you might want to use actual user authentication)
 */
export function generateUserId(): string {
  // In a real app, this would come from your authentication system
  const existingUserId = localStorage.getItem('nexgo_chat_user_id');
  if (existingUserId) {
    return existingUserId;
  }

  const newUserId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  localStorage.setItem('nexgo_chat_user_id', newUserId);
  return newUserId;
}

/**
 * Clear stored user ID (for logout or reset)
 */
export function clearUserId(): void {
  localStorage.removeItem('nexgo_chat_user_id');
}

/**
 * Validate chat request before sending
 */
export function validateChatRequest(request: ChatRequest): string[] {
  const errors: string[] = [];

  if (!request.user_id || request.user_id.trim() === '') {
    errors.push('User ID is required');
  }

  if (!request.session_id || request.session_id.trim() === '') {
    errors.push('Session ID is required');
  }

  // Message is required, but if audio_message is provided, we can use a default message
  if (!request.message || request.message.trim() === '') {
    if (!request.audio_message) {
      errors.push('Message is required');
    }
  }

  // Message length validation removed - messages can be of any length

  return errors;
}

/**
 * Send a chat message to get the main response (first API)
 */
export async function sendChatResponse(
  request: ChatRequest
): Promise<ChatResponse> {
  try {
    const response = await chatApi.post<ChatResponse>(
      CHAT_RESPONSE_ENDPOINT,
      request
    );

    if (!response.data) {
      throw new Error(ERROR_MESSAGES.INVALID_RESPONSE);
    }

    const { session_id, user_id, response: chatResponse } = response.data;

    if (!session_id || !user_id || !chatResponse) {
      throw new Error(ERROR_MESSAGES.MISSING_REQUIRED_FIELDS);
    }

    return response.data;
  } catch (error) {
    console.error('❌ [API ERROR] Chat Response API Error:', error);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError.code === 'ECONNABORTED') {
        throw new Error(ERROR_MESSAGES.TIMEOUT);
      }
      const errorData = axiosError.response?.data as
        | { message?: string }
        | undefined;
      const errorMessage = errorData?.message || axiosError.message;
      throw new Error(`${ERROR_MESSAGES.CHAT_SERVICE_ERROR}: ${errorMessage}`);
    }
    throw error;
  }
}

/**
 * Fetch follow-up questions (second API)
 */
export async function fetchChatFollowUps(
  request: FollowUpsRequest
): Promise<FollowUpsResponse | null> {
  try {
    const response = await chatApi.post<FollowUpsResponse>(
      CHAT_FOLLOW_UPS_ENDPOINT,
      request
    );
    return response.data;
  } catch (error) {
    console.error(
      '⚠️ [API ERROR] Chat Follow-ups API Error (non-critical):',
      error
    );
    // Return empty follow-ups on error (non-critical)
    return { followUpQuestions: [], status: 'error' };
  }
}

/**
 * Fetch experiences (third API)
 */
export async function fetchChatExperiences(
  request: ExperiencesRequest
): Promise<ExperiencesResponse | null> {
  try {
    const response = await chatApi.post<ExperiencesResponse>(
      CHAT_EXPERIENCES_ENDPOINT,
      request
    );
    return response.data;
  } catch (error) {
    console.error(
      '⚠️ [API ERROR] Chat Experiences API Error (non-critical):',
      error
    );
    // Return empty experiences on error (non-critical)
    return { experiences: [], status: 'error' };
  }
}

/**
 * Streaming chat callbacks
 */
export interface StreamCallbacks {
  onText?: (content: string) => void;
  onMetadata?: (data: unknown) => void;
  onInteraction?: (data: unknown) => void;
  onDone?: () => void;
  onError?: (error: string) => void;
  onSessionId?: (sessionId: string) => void;
}

/**
 * Process a single SSE line (helper function)
 * Returns true if stream should stop (done event), false otherwise
 */
function processSSELine(jsonStr: string, callbacks: StreamCallbacks): boolean {
  const startTime = performance.now();
  try {
    const parseStartTime = performance.now();
    const event: StreamEvent = JSON.parse(jsonStr);
    const parseTime = performance.now() - parseStartTime;

    console.log(`🔍 [STREAM] SSE event parsed:`, {
      eventType: event.type,
      hasContent: !!event.content,
      contentLength: event.content?.length || 0,
      hasData: !!event.data,
      parseTime: `${parseTime.toFixed(2)}ms`,
      timestamp: new Date().toISOString(),
    });

    // Handle different event types
    switch (event.type) {
      case 'text':
        if (event.content && callbacks.onText) {
          const textLength = event.content.length;
          const callbackStartTime = performance.now();
          console.log(`📝 [STREAM] Text event received:`, {
            length: textLength,
            preview: event.content.substring(0, 50),
            parseTime: `${parseTime.toFixed(2)}ms`,
            timestamp: new Date().toISOString(),
          });

          // Display text immediately as it arrives from the server
          // For very large server chunks (>100 chars), split synchronously for incremental display
          // Use queueMicrotask between chunks to avoid blocking the main thread
          const LARGE_CHUNK_THRESHOLD = 100; // Only chunk if server sends >100 chars at once
          const CHUNK_SIZE = 20; // Characters per chunk for large text

          if (textLength > LARGE_CHUNK_THRESHOLD) {
            // Server sent a large chunk - split it for incremental display
            // Process synchronously with queueMicrotask for non-blocking updates
            const totalChunks = Math.ceil(textLength / CHUNK_SIZE);
            console.log(`🔪 [STREAM] Splitting large text chunk:`, {
              totalLength: textLength,
              threshold: LARGE_CHUNK_THRESHOLD,
              chunkSize: CHUNK_SIZE,
              totalChunks,
              willUseQueueMicrotask: true,
            });

            let offset = 0;
            let chunkIndex = 0;
            const processNextChunk = () => {
              const remaining = textLength - offset;
              const chunkSize = Math.min(CHUNK_SIZE, remaining);
              const chunk = event.content.substring(offset, offset + chunkSize);

              if (chunk.length > 0) {
                chunkIndex++;
                console.log(
                  `🔪 [STREAM] Processing chunk ${chunkIndex}/${totalChunks}:`,
                  {
                    chunkIndex,
                    totalChunks,
                    chunkSize: chunk.length,
                    offset,
                    remaining,
                    chunkPreview: chunk.substring(0, 30),
                  }
                );

                // Send chunk immediately
                const callbackInvokeTime = performance.now();
                callbacks.onText(chunk);
                const callbackInvokeDuration =
                  performance.now() - callbackInvokeTime;

                offset += chunkSize;

                console.log(
                  `✅ [STREAM] Chunk ${chunkIndex} sent to callback:`,
                  {
                    chunkIndex,
                    chunkSize: chunk.length,
                    callbackInvokeDuration: `${callbackInvokeDuration.toFixed(2)}ms`,
                    totalProcessed: offset,
                    remaining: textLength - offset,
                  }
                );

                if (offset < textLength) {
                  // Use queueMicrotask for next chunk - immediate but non-blocking
                  const queueTime = performance.now();
                  queueMicrotask(() => {
                    const queueDelay = performance.now() - queueTime;
                    console.log(`⏭️ [STREAM] queueMicrotask executed:`, {
                      delay: `${queueDelay.toFixed(2)}ms`,
                      nextChunkIndex: chunkIndex + 1,
                    });
                    processNextChunk();
                  });
                } else {
                  const callbackTime = performance.now() - callbackStartTime;
                  console.log(`✅ [STREAM] Text callback executed (chunked):`, {
                    totalLength: textLength,
                    chunks: totalChunks,
                    callbackTime: `${callbackTime.toFixed(2)}ms`,
                    totalTime: `${(performance.now() - startTime).toFixed(2)}ms`,
                    avgTimePerChunk: `${(callbackTime / totalChunks).toFixed(2)}ms`,
                  });
                }
              }
            };

            // Start processing chunks immediately
            console.log(`🚀 [STREAM] Starting chunk processing...`);
            processNextChunk();
          } else {
            // Small/medium text from server - send immediately without chunking
            console.log(
              `⚡ [STREAM] Small text chunk - sending immediately (no chunking):`,
              {
                length: textLength,
                threshold: LARGE_CHUNK_THRESHOLD,
              }
            );
            const directCallbackTime = performance.now();
            callbacks.onText(event.content);
            const directCallbackDuration =
              performance.now() - directCallbackTime;
            const callbackTime = performance.now() - callbackStartTime;
            console.log(`✅ [STREAM] Text callback executed (direct):`, {
              length: textLength,
              callbackInvokeDuration: `${directCallbackDuration.toFixed(2)}ms`,
              callbackTime: `${callbackTime.toFixed(2)}ms`,
              totalTime: `${(performance.now() - startTime).toFixed(2)}ms`,
            });
          }
        }
        return false;

      case 'metadata':
        console.log(`📊 [STREAM] Metadata event received:`, {
          data: event.data,
          session_id: event.session_id,
          timestamp: new Date().toISOString(),
        });
        if (event.data && callbacks.onMetadata) {
          const callbackStartTime = performance.now();
          callbacks.onMetadata(event.data);
          const callbackTime = performance.now() - callbackStartTime;
          console.log(`✅ [STREAM] Metadata callback executed:`, {
            callbackTime: `${callbackTime.toFixed(2)}ms`,
          });
        }
        // Update session_id if provided in metadata
        if (
          event.data &&
          typeof event.data === 'object' &&
          'session_id' in event.data &&
          callbacks.onSessionId
        ) {
          const metadata = event.data as { session_id?: string };
          if (metadata.session_id) {
            callbacks.onSessionId(metadata.session_id);
          }
        }
        // Also check event.session_id directly
        if (event.session_id && callbacks.onSessionId) {
          callbacks.onSessionId(event.session_id);
        }
        return false;

      case 'interaction':
        if (event.data && callbacks.onInteraction) {
          callbacks.onInteraction(event.data);
        }
        return false;

      case 'done':
        if (callbacks.onDone) {
          callbacks.onDone();
        }
        return true; // Stream is complete

      case 'error':
        const errorMessage =
          event.content || 'An error occurred during streaming';
        if (callbacks.onError) {
          callbacks.onError(errorMessage);
        }
        return true; // Stop on error

      default:
        console.warn('Unknown stream event type:', event.type);
        return false;
    }
  } catch (parseError) {
    console.error('Error parsing SSE event:', parseError, 'JSON:', jsonStr);
    // Continue processing other events
    return false;
  }
}

/**
 * Send a streaming chat message using SSE
 * This function handles Server-Sent Events from the streaming endpoint
 */
export async function sendChatStream(
  request: ChatRequest,
  callbacks: StreamCallbacks
): Promise<void> {
  // Use Next.js API route to proxy the streaming endpoint (handles CORS)
  const STREAMING_API_URL = '/api/chat/stream';

  try {
    const response = await fetch(STREAMING_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        user_id: request.user_id,
        session_id: request.session_id,
        message: request.message || '',
        ...(request.audio_message && { audio_message: request.audio_message }),
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Streaming request failed: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let chunkCount = 0;
    let totalBytesReceived = 0;
    const streamStartTime = performance.now();

    console.log('🚀 [STREAM] Starting to read stream...', {
      timestamp: new Date().toISOString(),
      url: STREAMING_API_URL,
      requestBody: {
        user_id: request.user_id,
        session_id: request.session_id,
        messageLength: request.message?.length || 0,
        hasAudioMessage: !!request.audio_message,
      },
    });

    try {
      while (true) {
        const readStartTime = performance.now();
        const { done, value } = await reader.read();
        const readTime = performance.now() - readStartTime;

        if (done) {
          console.log('🏁 [STREAM] Stream ended:', {
            chunkCount,
            totalBytes: totalBytesReceived,
            bufferLength: buffer.length,
            totalTime: `${(performance.now() - streamStartTime).toFixed(2)}ms`,
            timestamp: new Date().toISOString(),
          });
          // Process any remaining buffer
          if (buffer.trim()) {
            console.log('📦 [STREAM] Processing remaining buffer:', {
              length: buffer.length,
            });
            const lines = buffer.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                processSSELine(line.slice(6), callbacks);
              }
            }
          }
          break;
        }

        chunkCount++;
        totalBytesReceived += value.length;
        const chunkStartTime = performance.now();

        // Decode the chunk and add to buffer
        const decodedChunk = decoder.decode(value, { stream: true });
        buffer += decodedChunk;
        const decodeTime = performance.now() - chunkStartTime;

        console.log(`📦 [STREAM] Chunk received from API:`, {
          chunkNumber: chunkCount,
          bytes: value.length,
          decodedLength: decodedChunk.length,
          bufferLength: buffer.length,
          readTime: `${readTime.toFixed(2)}ms`,
          decodeTime: `${decodeTime.toFixed(2)}ms`,
          chunkPreview: decodedChunk.substring(
            0,
            Math.min(100, decodedChunk.length)
          ),
          timestamp: new Date().toISOString(),
        });

        // Process complete lines immediately (SSE format: "data: {...}\n\n")
        // Process as soon as we have complete lines, don't wait for full chunks
        let linesProcessed = 0;
        let newlineIndex;

        // Log buffer state before processing
        if (buffer.length > 0 && buffer.length < 200) {
          console.log(`🔍 [STREAM] Buffer before processing:`, {
            length: buffer.length,
            preview: buffer.substring(0, Math.min(100, buffer.length)),
            hasNewline: buffer.includes('\n'),
            newlineCount: (buffer.match(/\n/g) || []).length,
          });
        }

        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIndex).trim();
          buffer = buffer.slice(newlineIndex + 1);

          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6); // Remove "data: " prefix
            const processStartTime = performance.now();

            console.log(`⚡ [STREAM] Processing SSE line:`, {
              lineNumber: linesProcessed + 1,
              lineLength: line.length,
              jsonLength: jsonStr.length,
              linePreview: line.substring(0, Math.min(100, line.length)),
              timestamp: new Date().toISOString(),
            });

            // Try to parse and identify event type before processing
            let eventType = 'unknown';
            try {
              const previewEvent = JSON.parse(jsonStr);
              eventType = previewEvent.type || 'unknown';
              console.log(`🔍 [STREAM] Event type identified:`, {
                type: eventType,
                hasContent: !!previewEvent.content,
                contentLength: previewEvent.content?.length || 0,
              });
            } catch (e) {
              console.warn(`⚠️ [STREAM] Could not preview event type:`, e);
            }

            const shouldStop = processSSELine(jsonStr, callbacks);
            const processTime = performance.now() - processStartTime;
            linesProcessed++;

            console.log(`⚡ [STREAM] Line processed:`, {
              lineNumber: linesProcessed,
              eventType,
              processTime: `${processTime.toFixed(2)}ms`,
              shouldStop,
              linePreview: line.substring(0, Math.min(50, line.length)),
            });

            if (shouldStop) {
              console.log('🛑 [STREAM] Stream stopped by event handler');
              return; // Stream is done or error occurred
            }
          } else if (line.length > 0) {
            // Log non-data lines for debugging
            console.log(`⚠️ [STREAM] Non-data line found:`, {
              linePreview: line.substring(0, Math.min(50, line.length)),
              startsWithData: line.startsWith('data'),
              lineLength: line.length,
            });
          }
        }

        // Log if buffer is growing without processing
        if (buffer.length > 5000 && linesProcessed === 0) {
          console.log(`⚠️ [STREAM] Large buffer without processing:`, {
            bufferLength: buffer.length,
            chunkNumber: chunkCount,
            bufferPreview: buffer.substring(0, 200),
            hasNewline: buffer.includes('\n'),
          });
        }

        if (linesProcessed > 0) {
          console.log(`✅ [STREAM] Chunk processing complete:`, {
            chunkNumber: chunkCount,
            linesProcessed,
            remainingBuffer: buffer.length,
            totalChunkTime: `${(performance.now() - chunkStartTime).toFixed(2)}ms`,
          });
        } else if (buffer.length > 1000) {
          // Log when buffer is growing but no lines are processed
          console.log(`📊 [STREAM] Chunk received but no lines processed:`, {
            chunkNumber: chunkCount,
            bufferLength: buffer.length,
            bufferEnd: buffer.substring(Math.max(0, buffer.length - 100)),
          });
        }
      }

      // Call done callback if stream ends without explicit 'done' event
      if (callbacks.onDone) {
        callbacks.onDone();
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    console.error('Streaming chat error:', error);
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to establish streaming connection';
    if (callbacks.onError) {
      callbacks.onError(errorMessage);
    }
    throw error;
  }
}
